import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import { N8N_TEAMS_URL } from '@/api/n8n'
import { logN8nBurstTiming } from '@/utils/n8nBurstTiming'
import type { ChatMessage } from '@/types/chat'

const ACCOUNT_PATH = `${CHATWOOT_BASE}/api/v1/accounts/3`

function sortByCreatedAt(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((a, b) => a.created_at - b.created_at)
}

// Leitura de mensagens vai pelo comando n8n `get_conversation_client` (token de
// serviço do lado do servidor), não direto no Chatwoot com o token pessoal do
// agente — o dashboard lista conversas de qualquer time/inbox (via n8n,
// `get_conversations`), mas o token pessoal só enxerga os times/inboxes em que
// o agente é membro no Chatwoot, e retornava 401 ao abrir conversas de outros
// times. `before` é opcional (paginação "carregar mensagens mais antigas").
async function fetchConversationClient(convId: number, before?: number): Promise<{ payload?: ChatMessage[] } | null> {
  const res = await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command: 'get_conversation_client', conversation_id: convId, before, timestamp: Date.now() }),
  })
  // Fase 2 do plano de otimização de abertura de conversa (docs/planos/
  // PLANO-OTIMIZACAO-CARREGAMENTO-MENSAGENS.md) — mesma instrumentação de
  // n8nBurstTiming.ts já usada na Fase 4 (reaberta) de
  // PLANO-OTIMIZACAO-CARREGAMENTO-DASHBOARD.md (installN8nBurstFetchTap() já
  // fica ligada globalmente em main.ts atrás da mesma flag), só que disparada
  // aqui: mede se get_conversation_client (abrir conversa) fica em fila atrás
  // de get_conversations (polling da lista) quando os dois coincidem. Opt-in,
  // silencioso por padrão — só roda com localStorage.debug_n8n_timing === '1'.
  if (typeof window !== 'undefined' && window.localStorage?.getItem('debug_n8n_timing') === '1') {
    setTimeout(() => logN8nBurstTiming(), 300)
  }
  if (!res.ok) return null
  const data = await res.json()
  return Array.isArray(data) ? data[0] : data
}

/** Busca as mensagens mais recentes de uma conversa. Retorna null (não []) em caso de falha, para o chamador não sobrescrever o cache existente. */
export async function fetchMessages(convId: number): Promise<ChatMessage[] | null> {
  const data = await fetchConversationClient(convId)
  if (!data) return null
  return sortByCreatedAt(data.payload || [])
}

export interface OlderMessagesResult {
  /** Lista mesclada (mais antigas primeiro) — igual a `current` se a busca falhar ou não trouxer nada novo. */
  messages: ChatMessage[]
  /** true só quando a API confirmou que não há mais mensagens antigas (resposta ok e vazia/já vista). */
  noMore: boolean
}

/** Busca mensagens anteriores a `current[0]` (paginação "carregar mais antigas"), já deduplicadas e mescladas. */
export async function fetchOlderMessages(convId: number, current: ChatMessage[]): Promise<OlderMessagesResult> {
  if (!current.length) return { messages: current, noMore: false }
  const oldestId = current[0].id
  const data = await fetchConversationClient(convId, oldestId)
  if (!data) return { messages: current, noMore: false }
  const older = sortByCreatedAt(data.payload || [])
  if (!older.length) return { messages: current, noMore: true }
  const existingIds = new Set(current.map(m => m.id))
  const fresh = older.filter(m => !existingIds.has(m.id))
  if (!fresh.length) return { messages: current, noMore: true }
  return { messages: [...fresh, ...current], noMore: false }
}

/**
 * Busca o histórico COMPLETO de uma conversa (pagina pra trás via
 * fetchOlderMessages até esgotar) — usado pela exportação do Relatório de
 * Resolução, que precisa do transcript inteiro, não só o lote recente que
 * fetchMessages traz. Vai por N8N_TEAMS_URL (mesmo motivo de fetchMessages:
 * o token pessoal do agente pode não ter escopo pra ler conversas de outro
 * time/inbox, e este relatório cobre a conta inteira). Retorna null em caso
 * de falha na busca inicial (mesmo contrato de fetchMessages).
 */
export async function fetchAllMessages(convId: number): Promise<ChatMessage[] | null> {
  const initial = await fetchMessages(convId)
  if (!initial) return null

  let all = initial
  for (let i = 0; i < 100; i++) {
    const { messages, noMore } = await fetchOlderMessages(convId, all)
    all = messages
    if (noMore) break
  }
  return all
}

/** Envia uma mensagem de texto. Retorna a mensagem criada (com id do servidor), ou null em caso de falha. */
export async function postMessage(convId: number, token: string, content: string, isPrivate = false): Promise<ChatMessage | null> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ content, message_type: 'outgoing', private: isPrivate }),
  })
  if (!res.ok) return null
  return res.json()
}
